import React, { useState } from "react";
import { Search, Download, XCircle, Package, Truck, Zap, CheckCircle2, Clock, Home } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { api, formatINR } from "@/lib/api";
import { toast } from "sonner";

const STATUS_STYLES = {
  paid: "bg-brandter text-ok",
  shipped: "bg-warn/15 text-warn",
  delivered: "bg-ok/15 text-ok",
  cancelled: "bg-err/10 text-err",
};

const TIMELINE = [
  { key: "paid", label: "Paid", Icon: CheckCircle2 },
  { key: "shipped", label: "Shipped", Icon: Package },
  { key: "out_for_delivery", label: "Out for Delivery", Icon: Truck },
  { key: "delivered", label: "Delivered", Icon: Home },
];

function OrderTimeline({ order }) {
  if (order.status === "cancelled") {
    return (
      <div className="mt-4 flex items-center gap-2 text-sm font-semibold text-err" data-testid={`timeline-${order.id}`}>
        <XCircle size={16} /> Order cancelled
      </div>
    );
  }
  let reached = 1; // paid
  if (order.status === "shipped") reached = order.out_for_delivery ? 3 : 2;
  if (order.status === "delivered") reached = 4;
  return (
    <div className="mt-4" data-testid={`timeline-${order.id}`}>
      <div className="flex items-start">
        {TIMELINE.map((s, i) => {
          const done = i < reached;
          const current = i === reached - 1;
          return (
            <React.Fragment key={s.key}>
              <div className="flex flex-col items-center" data-testid={`timeline-step-${order.id}-${s.key}${done ? "-done" : ""}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${done ? "bg-brand text-white" : "bg-mist text-muted2"} ${current ? "ring-4 ring-brand/20" : ""}`}>
                  <s.Icon size={15} />
                </div>
                <span className={`text-[10px] mt-1 text-center leading-tight w-16 ${done ? "text-ink font-semibold" : "text-muted2"}`}>{s.label}</span>
              </div>
              {i < TIMELINE.length - 1 && <div className={`flex-1 h-0.5 mt-4 ${i < reached - 1 ? "bg-brand" : "bg-line"}`} />}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

export default function Orders() {
  const [phone, setPhone] = useState("");
  const [orders, setOrders] = useState(null);
  const [loading, setLoading] = useState(false);

  const lookup = async (e) => {
    e?.preventDefault();
    if (phone.replace(/\D/g, "").length < 10) { toast.error("Enter a valid 10-digit mobile number"); return; }
    setLoading(true);
    try {
      const list = await api.lookupOrders(phone);
      setOrders(list);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const cancel = async (id) => {
    try {
      await api.cancelOrder(id, phone);
      toast.success("Order cancelled");
      lookup();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const requestBv = async (id) => {
    if (!window.confirm("Request instant BV crediting? Once requested, this order can no longer be cancelled.")) return;
    try {
      await api.requestInstantBv(id);
      toast.success("Instant BV requested — our team will credit it shortly");
      lookup();
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <div className="min-h-screen bg-paper">
      <Header />
      <main className="mx-auto max-w-3xl px-4 sm:px-6 py-10">
        <h1 className="font-display text-4xl sm:text-5xl text-ink mb-2">Track My Orders</h1>
        <p className="text-muted2 mb-6">Enter the mobile number you used at checkout.</p>

        <form onSubmit={lookup} className="flex gap-2 max-w-md">
          <div className="relative flex-1">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted2" size={18} />
            <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Mobile number" className="w-full h-12 rounded-full border border-line pl-10 pr-4 text-sm focus:outline-none focus:border-brand" data-testid="orders-phone-input" />
          </div>
          <button type="submit" className="h-12 px-6 rounded-full bg-brand text-white font-semibold" data-testid="orders-lookup-button">Find</button>
        </form>

        {loading && <p className="text-muted2 mt-8">Searching…</p>}

        {orders !== null && !loading && (
          <div className="mt-8 space-y-4">
            {orders.length === 0 && <p className="text-muted2">No orders found for this number.</p>}
            {orders.map((o) => (
              <div key={o.id} className="rounded-2xl border border-line p-5" data-testid={`order-card-${o.id}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Package size={18} className="text-brand" />
                    <span className="font-mono font-semibold text-ink text-sm">{o.id.slice(0, 8).toUpperCase()}</span>
                  </div>
                  <span className={`text-xs font-semibold px-2.5 py-1 rounded-full capitalize ${STATUS_STYLES[o.status] || "bg-mist text-muted2"}`}>{o.status}</span>
                </div>
                <p className="text-xs text-muted2 mt-1">{(o.created_at || "").slice(0, 10)}</p>
                <div className="mt-3 space-y-1 text-sm">
                  {o.items.map((it, i) => (
                    <div key={`${o.id}-${it.name}-${i}`} className="flex justify-between"><span className="text-ink/80">{it.name} × {it.quantity}</span><span className="text-ink">{formatINR(it.line_total)}</span></div>
                  ))}
                </div>
                <div className="border-t border-line mt-3 pt-3 flex items-center justify-between">
                  <span className="font-semibold text-ink">{formatINR(o.total_billing || o.amount)}</span>
                  {o.cod_due > 0 && <span className="text-xs text-warn font-semibold">COD due: {formatINR(o.cod_due)}</span>}
                </div>

                <OrderTimeline order={o} />

                {o.altos_verified && (
                  <div className="mt-3 rounded-lg bg-brandter/60 border border-ok/20 px-3 py-2" data-testid={`bv-block-${o.id}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm text-ink flex items-center gap-1.5"><Zap size={15} className="text-brand" /> BV points: <b>{o.total_bv || 0}</b></span>
                      {o.instant_bv_processed ? (
                        <span className="text-xs font-semibold text-ok flex items-center gap-1"><CheckCircle2 size={14} /> BV credited</span>
                      ) : o.instant_bv_requested ? (
                        <span className="text-xs font-semibold text-warn flex items-center gap-1"><Clock size={14} /> BV requested</span>
                      ) : o.status !== "cancelled" ? (
                        <button onClick={() => requestBv(o.id)} data-testid={`request-bv-${o.id}`} className="text-xs font-semibold text-white bg-brand rounded-full px-3 py-1.5 flex items-center gap-1"><Zap size={13} /> Request instant BV</button>
                      ) : null}
                    </div>
                  </div>
                )}
                {o.tracking_url && (
                  <a href={o.tracking_url} target="_blank" rel="noopener noreferrer" className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-brand"><Truck size={15} /> Track shipment {o.courier_name ? `(${o.courier_name})` : ""}</a>
                )}
                <div className="flex gap-3 mt-3">
                  {o.status !== "cancelled" && (
                    <a href={api.invoiceUrl(o.id)} target="_blank" rel="noopener noreferrer" className="text-sm font-semibold text-ink flex items-center gap-1"><Download size={15} /> Invoice</a>
                  )}
                  {["paid"].includes(o.status) && !o.instant_bv_requested && (
                    <button onClick={() => cancel(o.id)} className="text-sm font-semibold text-err flex items-center gap-1" data-testid={`cancel-order-${o.id}`}><XCircle size={15} /> Cancel order</button>
                  )}
                  {o.instant_bv_requested && <span className="text-xs text-muted2">Instant BV requested — cannot be cancelled</span>}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
